<template>
  <v-card class="map-selector-card">
    <v-card-title class="d-flex align-center">
      <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
      <span>Seleccionar dirección de entrega</span>
    </v-card-title>
    <v-card-subtitle>
      Ingresá tu dirección para calcular el costo de envío
    </v-card-subtitle>
    <v-card-text v-if="cargandoGoogleMap" class="scrollable-content">
      <div class="text-center py-4">
        <v-progress-circular
          :size="50"
          color="primary"
          indeterminate
        ></v-progress-circular>
        <p class="mt-3 grey--text">Cargando mapa...</p>
      </div>
    </v-card-text>
    <v-card-text v-else class="scrollable-content">
      <!-- Modo búsqueda por texto (por defecto) -->
      <div v-if="!ingresarUbicacion">
        <v-alert type="info" dense outlined icon="mdi-information" class="mb-4">
          <div class="d-flex align-center">
            <span>Escribí tu dirección y seleccioná una opción de la lista</span>
          </div>
        </v-alert>
        <GmapAutocomplete
          ref="gmapAuto"
          :placeholder="'Ej: Av. Colón 1234, Córdoba'"
          :types="['address']"
          :componentRestrictions="{ country: 'AR' }"
          @place_changed="setPlace"
        />
        <v-btn
          v-if="currentPlace && !markCreated"
          block
          color="primary"
          class="mt-3"
          @click="addMarker"
          :loading="loading"
          :disabled="loading"
        >
          <v-icon left>mdi-map-search</v-icon>
          Ver en el mapa
        </v-btn>
      </div>

      <!-- Modo selección en mapa -->
      <div v-else>
        <v-alert type="info" dense outlined icon="mdi-cursor-pointer" class="mb-4">
          <div class="d-flex align-center">
            <span>Hacé clic en el mapa para seleccionar tu dirección</span>
          </div>
        </v-alert>
      </div>

      <!-- Switch para cambiar entre modos -->
      <v-divider class="my-4"></v-divider>
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon :color="ingresarUbicacion ? 'grey' : 'primary'" class="mr-2">mdi-text-box</v-icon>
          <span class="text-caption" :class="ingresarUbicacion ? 'grey--text' : 'primary--text font-weight-bold'">
            Buscar por texto
          </span>
        </div>
        <v-switch
          v-model="ingresarUbicacion"
          class="ma-0"
          hide-details
          color="primary"
        ></v-switch>
        <div class="d-flex align-center">
          <span class="text-caption" :class="!ingresarUbicacion ? 'grey--text' : 'primary--text font-weight-bold'">
            Seleccionar en mapa
          </span>
          <v-icon :color="!ingresarUbicacion ? 'grey' : 'primary'" class="ml-2">mdi-map</v-icon>
        </div>
      </div>

      <!-- Mapa -->
      <GmapMap
        ref="map"
        :options="{
          streetViewControl: false,
          fullscreenControl: false
        }"
        :center="center"
        :zoom="zoom"
        style="width: 100%; height: 300px; margin-top: 20px; border-radius: 4px; border: 2px solid #e0e0e0;"
        @click="addMarkerFromGmap"
      >
        <GmapMarker
          :key="index"
          v-for="(m, index) in markers"
          :position="m.position"
          @click="openInfoWindow(m)"
        />
      </GmapMap>

      <!-- Mensaje de dirección seleccionada -->
      <v-alert
        v-if="markCreated && currentPlace && !domicilioNoEncontrado"
        type="success"
        dense
        outlined
        icon="mdi-check-circle"
        class="mt-3"
      >
        <div class="d-flex align-center">
          <div>
            <strong>Dirección seleccionada:</strong>
            <div class="text-caption">{{ currentPlace.formatted_address || currentPlace.name }}</div>
          </div>
        </div>
      </v-alert>

      <!-- Mensaje de zona no cubierta -->
      <v-alert
        v-if="domicilioNoEncontrado"
        type="warning"
        dense
        outlined
        class="mt-3"
      >
        <div class="d-flex align-center">
          <v-icon small class="mr-2">mdi-alert</v-icon>
          <div>
            <strong>No realizamos entregas en esta zona</strong>
            <div class="text-caption mt-1">Podés retirar tu pedido en el local</div>
          </div>
        </div>
      </v-alert>
    </v-card-text>

    <!-- Botones de acción - Footer fijo -->
    <v-divider class="footer-divider"></v-divider>
    <v-card-actions class="footer-actions pa-3">
      <v-btn
        text
        @click="$emit('cerrar-map-selector')"
        :disabled="loading"
      >
        Cancelar
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        v-if="domicilioNoEncontrado"
        color="primary"
        @click="retirar"
        :disabled="loading"
      >
        <v-icon left>mdi-store</v-icon>
        Retirar en el local
      </v-btn>
      <v-btn
        v-else-if="markCreated"
        color="primary"
        @click="onAddressConfirmed"
        :loading="loading"
        :disabled="loading"
      >
        <v-icon left>mdi-check</v-icon>
        Confirmar dirección
      </v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { gmapApi } from 'vue2-google-maps'
import { mapActions, mapGetters } from 'vuex'
import { bus } from '@/helpers/bus.js'

export default {
  name: 'GoogleMapSelector',
  props: {
    /** Si es true, confirma la ubicación sin validar polígonos de envío (p. ej. guardar domicilios en Mi cuenta). */
    omitirValidacionZona: {
      type: Boolean,
      default: false
    },
    /** Opcional: mostrar una ubicación ya geolocalizada (modo editar domicilio). */
    initialAddressText: {
      type: String,
      default: ''
    },
    initialLat: {
      type: [Number, String],
      default: null
    },
    initialLng: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    google: gmapApi,
    ...mapGetters({
      clientData: 'App/data'
    })
  },
  async mounted () {
    const { data } = await this.getZonas()
    if (this.clientData && this.clientData.Lat && this.clientData.Long) {
      this.center.lat = Number(this.clientData.Lat)
      this.center.lng = Number(this.clientData.Long)
      this.zoom = 18
    }
    this.$gmapApiPromiseLazy().then(() => {
      const { Polygon } = this.google.maps
      this.zonas = data.map((e) => {
        return {
          id: e.id,
          Nombre: e.Nombre,
          Color: e.Color,
          Precio: e.Precio,
          Lista: e.ListaCoordenada
        }
      })
      this.poligonos = this.zonas.map(e => {
        return new Polygon({
          paths: e.Lista.map(l => {
            return {
              lat: parseFloat(l.Latitud),
              lng: parseFloat(l.Longitud)
            }
          }),
          strokeColor: e.Color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: e.Color,
          fillOpacity: 0.35
        })
      })
      this.cargandoGoogleMap = false

      // Si viene una dirección existente (editar), mostrarla centrada y marcada.
      this.applyInitialLocation()
    })
  },
  beforeDestroy () {
    this.currentPlace = null
    this.loading = false
  },
  data () {
    return {
      center: { lat: -31.412115135505516, lng: -64.18775411971284 },
      currentPlace: null,
      markers: [],
      zoom: 13,
      loading: false,
      markCreated: false,
      zonas: [],
      poligonos: [],
      domicilioNoEncontrado: false,
      cargandoGoogleMap: true,
      ingresarUbicacion: false,
      infoWindow: null
    }
  },
  methods: {
    ...mapActions({
      getZonas: 'App/setZonasCliente'
    }),
    setPlace (place) {
      this.domicilioNoEncontrado = false
      this.currentPlace = place
      // Automáticamente buscar en el mapa cuando se selecciona una dirección
      if (place && place.geometry) {
        this.addMarker()
      }
    },
    setAutocompleteText (text) {
      const t = text != null ? String(text) : ''
      const auto = this.$refs && this.$refs.gmapAuto ? this.$refs.gmapAuto : null
      if (!auto || !auto.$el) return
      const input = auto.$el.querySelector ? auto.$el.querySelector('input') : null
      if (input) input.value = t
    },
    applyInitialLocation () {
      const latN = this.initialLat != null ? Number(this.initialLat) : null
      const lngN = this.initialLng != null ? Number(this.initialLng) : null
      // Reset siempre para no reutilizar estado anterior (ej: editar otra dirección)
      this.markers = []
      this.markCreated = false
      this.currentPlace = null
      this.domicilioNoEncontrado = false

      if (latN == null || lngN == null || Number.isNaN(latN) || Number.isNaN(lngN)) {
        this.$nextTick(() => this.setAutocompleteText(this.initialAddressText))
        return
      }

      this.center.lat = latN
      this.center.lng = lngN
      this.zoom = 18
      this.markers = [{ position: { lat: latN, lng: lngN } }]
      this.markCreated = true
      this.domicilioNoEncontrado = false

      // Construir un place mínimo para que el UI muestre "Dirección seleccionada".
      const location = new this.google.maps.LatLng(latN, lngN)
      this.currentPlace = {
        formatted_address: this.initialAddressText || 'Dirección actual',
        name: this.initialAddressText || 'Dirección actual',
        geometry: { location }
      }

      // Completar el textbox para permitir editar desde ahí.
      this.$nextTick(() => this.setAutocompleteText(this.initialAddressText))
    },
    addMarker () {
      this.markers = []
      this.infoWindow = null
      if (this.currentPlace) {
        this.loading = true
        setTimeout(() => {
          const marker = {
            lat: this.currentPlace.geometry.location.lat(),
            lng: this.currentPlace.geometry.location.lng()
          }
          this.markers.push({ position: marker })
          this.center = marker
          this.zoom = 18
          this.markCreated = true
          this.loading = false
        }, 1500)
      }
    },
    addMarkerFromGmap (event) {
      if (!this.ingresarUbicacion) return
      if (this.infoWindow) {
        this.infoWindow.close()
        this.infoWindow = null
      }
      this.markers = []
      const latLng = event.latLng
      const marker = {
        lat: latLng.lat(),
        lng: latLng.lng()
      }
      this.zoom = 18
      this.markers.push({ position: marker })
      const geocoder = new this.google.maps.Geocoder()

      geocoder.geocode({ location: latLng }, (results, status) => {
        const addressResults = results.filter(result => result.types.includes('street_address'))
        if (status === this.google.maps.GeocoderStatus.OK && addressResults[0]) {
          this.currentPlace = addressResults[0]
          this.infoWindow = new this.google.maps.InfoWindow()
          this.infoWindow.setContent(`<div><strong>Dirección:</strong> ${this.currentPlace.formatted_address}</div>`)
          this.infoWindow.setPosition(latLng)
          this.infoWindow.open(this.$refs.map.$mapObject.data.map)
        } else {
          // Usar el lugar seleccionado aunque no tenga street_address
          if (results && results.length > 0) {
            this.currentPlace = results[0]
            this.infoWindow = new this.google.maps.InfoWindow()
            this.infoWindow.setContent(`<div><strong>Dirección:</strong> ${this.currentPlace.formatted_address || 'Ubicación seleccionada'}</div>`)
            this.infoWindow.setPosition(latLng)
            this.infoWindow.open(this.$refs.map.$mapObject.data.map)
          } else {
            this.infoWindow = null
          }
        }
      })
      this.markCreated = true
    },
    retirar () {
      bus.$emit('retirar-pedido', true)
      this.currentPlace = null
      this.$emit('cerrar-map-selector')
    },
    onAddressConfirmed () {
      try {
        if (this.omitirValidacionZona) {
          if (!this.currentPlace || !this.currentPlace.geometry) {
            this.loading = false
            return
          }
          this.loading = true
          const lat = this.currentPlace.geometry.location.lat()
          const lng = this.currentPlace.geometry.location.lng()
          const fullAddress = this.currentPlace.formatted_address || this.currentPlace.name || ''
          const googleMapsUrl = `https://www.google.com/maps/place/${encodeURIComponent(fullAddress)}/@${lat},${lng},17z`
          // Cerrar el mapa antes de emitir lat/lng al padre: si el padre usa :key con coordenadas,
          // actualizar Lat/Lng remontaba el selector y obligaba a confirmar dos veces.
          this.currentPlace = null
          this.markCreated = false
          this.markers = []
          this.loading = false
          this.$emit('cerrar-map-selector')
          this.$nextTick(() => {
            bus.$emit('onDomicilioGoogleMapSelectorChange', fullAddress)
            bus.$emit('onGoogleMapsUrlChange', googleMapsUrl)
            bus.$emit('onGoogleMapsLatLngChange', { lat, lng })
          })
          return
        }
        this.loading = true
        let costoEnvio = 0
        let fueraRangoEntrega = true
        for (const indice in this.poligonos) {
          const poligono = this.poligonos[indice]
          const resultPath = this.google.maps.geometry.poly.containsLocation(
            this.currentPlace.geometry.location,
            poligono
          )
          if (resultPath) {
            costoEnvio = parseInt(this.zonas[indice].Precio)
            fueraRangoEntrega = false

            // Generar URL de Google Maps con la dirección seleccionada
            const lat = this.currentPlace.geometry.location.lat()
            const lng = this.currentPlace.geometry.location.lng()
            const address = this.currentPlace.name || this.currentPlace.formatted_address
            const fullAddress = this.currentPlace.formatted_address || address
            const googleMapsUrl = `https://www.google.com/maps/place/${encodeURIComponent(fullAddress)}/@${lat},${lng},17z`

            // Emitir tanto la dirección como la URL de Google Maps
            bus.$emit('onDomicilioGoogleMapSelectorChange', address)
            bus.$emit('onGoogleMapsUrlChange', googleMapsUrl)
            bus.$emit('onGoogleMapsLatLngChange', { lat, lng })

            this.$emit('fuera-rango-entrega', fueraRangoEntrega)
            bus.$emit('nuevoCostoEnvio', costoEnvio)
            setTimeout(() => {
              this.$emit('cerrar-map-selector')
              this.currentPlace = null
            }, 1000)
            return
          }
        }
        this.loading = false
        this.domicilioNoEncontrado = true
      } catch (e) {
        console.error(e)
        this.loading = false
      }
    },
    openInfoWindow (m) {
      this.center = m.position
      if (this.infoWindow) {
        this.infoWindow.open(this.$refs.map, this.markers[0])
      }
    }
  },
  watch: {
    initialAddressText (val) {
      // Mantener el texto sincronizado cuando el diálogo se abre en modo edición.
      this.$nextTick(() => this.setAutocompleteText(val))
    },
    initialLat () {
      // Si cambia de dirección a editar, refrescar marcador/centro.
      if (this.cargandoGoogleMap) return
      this.applyInitialLocation()
    },
    initialLng () {
      if (this.cargandoGoogleMap) return
      this.applyInitialLocation()
    },
    ingresarUbicacion (newValue, oldValue) {
      // Limpiar marcadores y estado al cambiar de modo
      this.markers = []
      if (this.infoWindow) {
        this.infoWindow.close()
        this.infoWindow = null
      }
      this.markCreated = false
      this.currentPlace = null
      this.domicilioNoEncontrado = false

      // Ajustar zoom al cambiar de modo
      if (newValue) {
        // Modo mapa: zoom más amplio para facilitar la selección
        this.zoom = 15
      } else {
        // Modo búsqueda: volver al zoom inicial
        if (this.clientData && this.clientData.Lat && this.clientData.Long) {
          this.zoom = 18
        } else {
          this.zoom = 13
        }
      }
    }
    // $refs watcher removed: noisy and unnecessary
  }
}
</script>
<style scoped>
.map-selector-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  height: 90vh;
}

.scrollable-content {
  overflow-y: auto;
  flex: 1;
  padding-bottom: 16px;
}

.footer-divider {
  flex-shrink: 0;
}

.footer-actions {
  flex-shrink: 0;
  background-color: white;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.pac-target-input {
  margin-top: 10px;
  padding: 12px 16px;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  font-size: 16px;
  transition: border-color 0.3s;
}

.pac-target-input:focus {
  border-color: var(--v-primary-base);
  outline: none;
}

.pac-container {
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
}

.pac-item {
  padding: 12px;
  cursor: pointer;
}

.pac-item:hover {
  background-color: #f5f5f5;
}

.pac-item-selected {
  background-color: #e3f2fd;
}

/* Ajustes para móvil */
@media (max-width: 600px) {
  .map-selector-card {
    max-height: 100vh;
    height: 100vh;
  }
}
</style>
